import { SceneDTO, TiledMapDTO } from "../../../shared/dtos";

/**
 * Raised when a scene payload cannot produce a loadable Tiled map.
 *
 * Phaser's own failure for this is `URL Error in File: <sceneId> from: null`, thrown from
 * deep inside the loader with no indication of which record is at fault. This carries the
 * scene/project identifiers instead, so the broken document can be found directly.
 */
export class SceneMapError extends Error {
    constructor(
        message: string,
        public readonly sceneId: string
    ) {
        super(message);
        this.name = "SceneMapError";
        // Required for `instanceof` to work when targeting ES5/ES2018 via ts-jest.
        Object.setPrototypeOf(this, SceneMapError.prototype);
    }
}

/**
 * Guard the scene payload before it reaches `load.tilemapTiledJSON`, which accepts a null
 * url and only fails later inside the loader.
 *
 * The backend resolves `tiled_map` by looking up `scene.map_id` within the scene's own
 * project (`to_scene_dto_with_map`). That lookup is project-scoped, so a scene whose
 * `map_id` names a map that does not exist in its project serialises to `tiled_map: null`
 * rather than erroring — which is why this has to be checked here.
 */
export function assertMapDataPresent(
    scene: SceneDTO | null | undefined,
    sceneId: string
): asserts scene is SceneDTO {
    if (!scene) {
        throw new SceneMapError(
            `Scene "${sceneId}" returned no data. The scene endpoint responded but its ` +
                `payload was empty — check GET /project/{projectId}/scene/${sceneId}.`,
            sceneId
        );
    }

    if (!scene.tiled_map) {
        throw new SceneMapError(
            `Scene "${sceneId}" (project "${scene.project_id ?? "unknown"}") has no tiled_map. ` +
                `The backend resolves it from the scene's map_id within its own project, so ` +
                `this means map_id points at a map that does not exist in that project. ` +
                `Re-save the scene from the editor, or correct its map_id.`,
            sceneId
        );
    }
}

/**
 * Names of tilesets whose image has no matching entry in the scene's assets.
 *
 * Tileset textures are loaded from `scene.assets` under the key `tileset/<filename>`, while
 * layers bind to them via the map's own tileset list. When the two disagree,
 * `addTilesetImage` returns null and the failure only surfaces later as an opaque error
 * while creating a layer. Reporting the offending tileset names up front is far more useful.
 */
export function findUnbackedTilesets(
    tiledMap: TiledMapDTO,
    assets: { category: string; filename: string }[] | null | undefined
): string[] {
    const tilesetFilenames = new Set(
        (assets ?? [])
            .filter((asset) => asset.category === "tileset")
            .map((asset) => asset.filename)
    );

    return (tiledMap?.tilesets ?? [])
        .filter((tileset) => !tilesetFilenames.has(basename(tileset.image)))
        .map((tileset) => tileset.name);
}

/** Tiled stores tileset images relative to the map file; assets are keyed by filename alone. */
function basename(imagePath: string | null | undefined): string {
    if (!imagePath) {
        return "";
    }
    const normalized = imagePath.replace(/\\/g, "/");
    return normalized.slice(normalized.lastIndexOf("/") + 1);
}
